// Layered mech renderer (ARCHITECTURE "MechView layering"). Back → front:
//   rear equipment → rear arm (upper, fore; rearTint) → body → body glow (ADD)
//   → front equipment → weapon (+glow) → front arm (upper, fore) → fx layer.
// Frames are driven manually from update(dt); every frame places the
// equipment at the frame's attachment points and solves the arms: with
// aimWeight 1 the front arm aims and the rear arm reaches the foregrip (2-bone
// IK), otherwise the frame's arm poses are used (blended by aimWeight).
//
// Works standalone (hangar, results, preview): it needs only a scene. Missing
// art (mech, arm parts, weapon, equipment) falls back to generated placeholders.

import Phaser from 'phaser';
import { COLORS, mix } from '../../config/palette';
import type { MechViewApi } from '../../core/contracts';
import { save } from '../../core/save';
import {
  ANIM_KEYS,
  EQUIP_SLOT_OF,
  type AnimKey,
  type AttachKey,
  type EquipId,
  type MechArt,
  type MechFrameMeta,
  type MechId,
  type VisualPiece,
  type WeaponArt,
  type WeaponId,
} from '../../core/types';
import { art } from '../../art/registry';
import { paintedEquipment, requestLoadoutArt } from '../../art/mech';
import { SUPPORT_STRETCH, supportReachLimit } from '../../art/mech/compose';
import { MECHS } from '../../data/mechs';
import { WEAPONS } from '../../data/items';
import { clamp, clamp01 } from './movement';
import { forwardKinematics, lerpAngle, solveTwoBone, toLocalAngle, toWorldAngle, twoBoneResult, wrapAngle } from './ik';
import {
  buildPlaceholderRig,
  placeholderMeta,
  placeholderPieces,
  placeholderWeapon,
  type AnimSrc,
  type RigSrc,
} from './placeholder';

/** Shoot layer duration (s): the shoot sheet plays across this window on recoil(). */
const RECOIL_TIME = 0.1;
/** Arm pull-back (px) per unit of the weapon runtime's kick. */
const KICK_PX = 5;
/** Muzzle rise (rad, negative = up) per unit of kick. */
const KICK_LIFT = -0.1;
const FLINCH_TIME = 0.2;
/** Aim is clamped to this local half-angle (a hair past vertical while facing hysteresis catches up). */
const AIM_LIMIT = Math.PI / 2 + 0.3;
/** Fraction of the arm's reach the front hand holds the grip at, per hold style. */
const HOLD_REACH: Record<'rifle' | 'heavy' | 'compact', number> = { rifle: 0.8, heavy: 0.68, compact: 0.9 };
/** Spin pivot above the foot anchor (px, ≈ body centre) for air tricks. */
const SPIN_PIVOT_Y = -46;
/** A full flip tuck compacts the body by this share (the legs fold through the jump pose). */
const TUCK_SHRINK = 0.1;
/** Rear equipment darkening (reads as further away). */
const REAR_EQUIP_TINT = 0x9aa2b4;

interface PieceSprite {
  piece: VisualPiece;
  sprite: Phaser.GameObjects.Image;
  glow: Phaser.GameObjects.Image | null;
}

/** Resolved arm part: sprite rotation offset when the texture is authored along +y instead of +x. */
interface ArmPart {
  sprite: Phaser.GameObjects.Image;
  glow: Phaser.GameObjects.Image | null;
  rotOffset: number;
}

function rotOffsetFor(scene: Phaser.Scene, key: string): number {
  // Arm parts point along +x (like weapons) by convention; a texture clearly
  // taller than wide is treated as authored along +y (like the leg bones).
  try {
    const src = scene.textures.get(key).getSourceImage() as { width: number; height: number };
    if (src && src.height > src.width * 1.25) return -Math.PI / 2;
  } catch {
    // fall through
  }
  return 0;
}

/** Turns MechArt from the registry into the internal rig source, validating every texture. */
function resolveRig(scene: Phaser.Scene, mechId: MechId): RigSrc {
  const a: MechArt | undefined = art.mechs[mechId];
  if (!a || !scene.textures.exists(a.sheetKey)) return buildPlaceholderRig(scene, mechId);
  const tex = scene.textures.get(a.sheetKey);
  const glowOk = !!a.glowSheetKey && scene.textures.exists(a.glowSheetKey);
  const anims = {} as Record<AnimKey, AnimSrc>;
  let fallbackAnim: AnimSrc | null = null;
  for (const key of ANIM_KEYS) {
    const info = a.anims?.[key];
    if (!info || info.count <= 0 || !tex.has(String(info.start))) continue;
    const frames: number[] = [];
    const meta: MechFrameMeta[] = [];
    for (let i = 0; i < info.count; i++) {
      const f = info.start + i;
      frames.push(tex.has(String(f)) ? f : frames[frames.length - 1] ?? info.start);
      meta.push(info.meta?.[i] ?? info.meta?.[info.meta.length - 1] ?? placeholderMeta(key, i));
    }
    anims[key] = { frames, fps: info.fps > 0 ? info.fps : 12, loop: info.loop, meta };
    if (!fallbackAnim || key === 'idle') fallbackAnim = anims[key];
  }
  if (!fallbackAnim) return buildPlaceholderRig(scene, mechId);
  for (const key of ANIM_KEYS) if (!anims[key]) anims[key] = fallbackAnim;

  const arm = a.arm;
  const armsOk = !!arm && scene.textures.exists(arm.upperKey) && scene.textures.exists(arm.foreKey);
  const ph = armsOk ? null : buildPlaceholderRig(scene, mechId);
  const glowKey = (k: string | undefined) => (k && scene.textures.exists(k) ? k : null);
  return {
    bodyKey: a.sheetKey,
    glowKey: glowOk ? a.glowSheetKey : null,
    anims,
    upperKey: ph ? ph.upperKey : arm.upperKey,
    foreKey: ph ? ph.foreKey : arm.foreKey,
    upperGlowKey: ph ? null : glowKey(arm.upperGlowKey),
    foreGlowKey: ph ? null : glowKey(arm.foreGlowKey),
    upperLen: ph ? ph.upperLen : arm.upperLen,
    foreLen: ph ? ph.foreLen : arm.foreLen,
    upperOrigin: ph ? ph.upperOrigin : arm.upperOrigin,
    foreOrigin: ph ? ph.foreOrigin : arm.foreOrigin,
    rearTint: arm?.rearTint ?? 0x8a93a6,
    cell: a.cell || 128,
    bodyScale: a.sheetScale ?? 1,
    armScale: ph ? 1 : (a.sheetScale ?? 1),
    footAnchor: a.footAnchor ?? { x: 64, y: 124 },
    placeholder: false,
  };
}

const ik = twoBoneResult();
const fk = twoBoneResult();

export class MechView implements MechViewApi {
  readonly root: Phaser.GameObjects.Container;
  readonly mechId: MechId;

  private readonly scene: Phaser.Scene;
  private readonly rig: RigSrc;
  /** Flinch / squash transform between the root (position, facing) and the parts. */
  private readonly inner: Phaser.GameObjects.Container;
  private readonly rearLayer: Phaser.GameObjects.Container;
  private readonly frontLayer: Phaser.GameObjects.Container;
  /** Foreground FX layer (abilities / item effects may add children). */
  readonly fxLayer: Phaser.GameObjects.Container;
  private readonly body: Phaser.GameObjects.Sprite;
  private readonly bodyGlow: Phaser.GameObjects.Sprite | null;
  private readonly upperR: ArmPart;
  private readonly foreR: ArmPart;
  private readonly upperF: ArmPart;
  private readonly foreF: ArmPart;
  private readonly weapon: Phaser.GameObjects.Image;
  private readonly weaponGlow: Phaser.GameObjects.Image;

  private pieces: PieceSprite[] = [];
  /** Painted equipment currently baked into the body art ("" = base art). */
  private loadoutSig = '';
  private weaponArt: WeaponArt | null = null;
  private weaponId: WeaponId | null = null;
  private holdReach = HOLD_REACH.rifle;
  private holdDrop = 0;

  private _anim: AnimKey = 'idle';
  private animSrc: AnimSrc;
  private animTime = 0;
  private timeScale = 1;
  private phaseMode = false;
  private frame = 0;
  private _animDone = false;

  private facing: 1 | -1 = 1;
  private scale = 1;
  /** MechDef.viewScale: this mech's display size relative to its baked sheet. */
  private readonly viewScale: number;
  private aimWorld = 0;
  private baseReactor: number;
  private reactorOverride: number | null = null;
  private reactor: number;

  private recoilT = RECOIL_TIME;
  private recoilAmount = 0;
  private kick = 0;
  private flinchT = 0;
  private flashT = 0;
  private squash = 0;
  /** Whole-body world rotation (rad, clockwise +) about the body centre (air tricks). */
  private spin = 0;
  /** 0..1 flip tuck: compacts the body slightly about its feet. */
  private tuck = 0;
  private destroyed = false;

  // Solved local (right-facing, foot-anchor origin) state of the current frame.
  private meta: MechFrameMeta;
  private gripX = 0;
  private gripY = 0;
  private weaponRot = 0;

  constructor(scene: Phaser.Scene, mechId: MechId) {
    this.scene = scene;
    this.mechId = mechId;
    this.rig = resolveRig(scene, mechId);
    this.baseReactor = MECHS[mechId]?.reactorColor ?? COLORS.volt;
    this.viewScale = MECHS[mechId]?.viewScale ?? 1;
    this.scale = this.viewScale;
    this.reactor = this.baseReactor;
    this.animSrc = this.rig.anims.idle;
    this.meta = this.animSrc.meta[0];

    const r = this.rig;
    const ox = r.footAnchor.x / r.cell;
    const oy = r.footAnchor.y / r.cell;
    this.root = scene.add.container(0, 0);
    this.inner = scene.add.container(0, 0);
    this.rearLayer = scene.add.container(0, 0);
    this.frontLayer = scene.add.container(0, 0);
    this.fxLayer = scene.add.container(0, 0);

    const armPart = (key: string, glowKey: string | null, origin: { x: number; y: number }): ArmPart => {
      // High-resolution arm textures are drawn at 1 / armScale (positions stay in rig units).
      const k = 1 / (r.armScale ?? 1);
      const sprite = scene.add.image(0, 0, key).setOrigin(origin.x, origin.y).setScale(k);
      const glow = glowKey ? scene.add.image(0, 0, glowKey).setOrigin(origin.x, origin.y).setScale(k).setBlendMode(Phaser.BlendModes.ADD) : null;
      return { sprite, glow, rotOffset: rotOffsetFor(scene, key) };
    };
    this.upperR = armPart(r.upperKey, r.upperGlowKey, r.upperOrigin);
    this.foreR = armPart(r.foreKey, r.foreGlowKey, r.foreOrigin);
    this.upperF = armPart(r.upperKey, r.upperGlowKey, r.upperOrigin);
    this.foreF = armPart(r.foreKey, r.foreGlowKey, r.foreOrigin);

    // The body sheet is baked at bodyScale texels per unit: draw it back at 1 / bodyScale.
    const bk = 1 / (r.bodyScale ?? 1);
    this.body = scene.add.sprite(0, 0, r.bodyKey, this.animSrc.frames[0]).setOrigin(ox, oy).setScale(bk);
    this.bodyGlow = r.glowKey
      ? scene.add.sprite(0, 0, r.glowKey, this.animSrc.frames[0]).setOrigin(ox, oy).setScale(bk).setBlendMode(Phaser.BlendModes.ADD)
      : null;
    this.weapon = scene.add.image(0, 0, '__DEFAULT').setVisible(false);
    this.weaponGlow = scene.add.image(0, 0, '__DEFAULT').setVisible(false).setBlendMode(Phaser.BlendModes.ADD);

    const children: Phaser.GameObjects.GameObject[] = [this.rearLayer, this.upperR.sprite];
    if (this.upperR.glow) children.push(this.upperR.glow);
    children.push(this.foreR.sprite);
    if (this.foreR.glow) children.push(this.foreR.glow);
    children.push(this.body);
    if (this.bodyGlow) children.push(this.bodyGlow);
    // The weapon sits between the arms: the near (front) arm and its fist wrap OVER the grip.
    children.push(this.frontLayer, this.weapon, this.weaponGlow, this.upperF.sprite);
    if (this.upperF.glow) children.push(this.upperF.glow);
    children.push(this.foreF.sprite);
    if (this.foreF.glow) children.push(this.foreF.glow);
    children.push(this.fxLayer);
    this.inner.add(children);
    this.root.add(this.inner);
    this.root.setScale(this.scale);

    this.applyTints();
    this.applyFrame();
  }

  // ── MechViewApi ──

  get currentAnim(): AnimKey {
    return this._anim;
  }

  get animDone(): boolean {
    return this._animDone;
  }

  /** Index of the displayed frame inside the current animation. */
  get frameIndex(): number {
    return this.frame;
  }

  get frameCount(): number {
    return this.animSrc.frames.length;
  }

  /** True when the view is rendering generated placeholder art. */
  get isPlaceholder(): boolean {
    return this.rig.placeholder;
  }

  /** Duration of one pass of an animation in seconds (at time scale 1). */
  animDuration(anim: AnimKey): number {
    const a = this.rig.anims[anim];
    return a.frames.length / Math.max(1, a.fps);
  }

  setPosition(x: number, feetY: number): void {
    if (this.destroyed) return;
    this.root.setPosition(x, feetY);
  }

  setFacing(f: 1 | -1): void {
    if (this.destroyed) return;
    this.facing = f;
    this.root.setScale(f * this.scale, this.scale);
  }

  setAim(angle: number): void {
    this.aimWorld = angle;
  }

  setWeapon(id: WeaponId | null): void {
    if (this.destroyed) return;
    this.weaponId = id;
    if (!id) {
      this.weaponArt = null;
      this.weapon.setVisible(false);
      this.weaponGlow.setVisible(false);
      return;
    }
    let wa = art.weapons[id];
    if (!wa || !this.scene.textures.exists(wa.key)) wa = placeholderWeapon(this.scene);
    this.weaponArt = wa;
    // A weapon's own hold (painted props) wins over its hold-style default.
    this.holdReach = wa.holdReach ?? HOLD_REACH[WEAPONS[id]?.holdStyle ?? 'rifle'] ?? HOLD_REACH.rifle;
    this.holdDrop = wa.holdDrop ?? 0;
    const wk = 1 / (wa.texScale ?? 1);
    this.weapon.setTexture(wa.key).setOrigin(wa.origin.x, wa.origin.y).setScale(wk).setVisible(true);
    if (wa.glowKey && this.scene.textures.exists(wa.glowKey)) {
      this.weaponGlow.setTexture(wa.glowKey).setOrigin(wa.origin.x, wa.origin.y).setScale(wk).setVisible(true);
    } else {
      this.weaponGlow.setVisible(false);
    }
    this.applyTints();
    this.applyFrame();
  }

  /**
   * Swap the body sheet and upper-arm textures for the re-baked "wearing this gear" art
   * (asynchronous: the current art stays until the bake finishes; stale bakes are ignored).
   */
  private applyLoadout(painted: string[]): void {
    const sig = painted.join('+');
    if (sig === this.loadoutSig) return;
    this.loadoutSig = sig;
    const r = this.rig;
    const useBase = (): void => this.setBodyArt(r.bodyKey, r.glowKey, r.upperKey, r.upperGlowKey, r.upperOrigin);
    if (painted.length === 0) {
      useBase();
      return;
    }
    void requestLoadoutArt(this.scene, this.mechId, painted).then((a) => {
      if (this.destroyed || this.loadoutSig !== sig) return;
      if (!a) useBase();
      else this.setBodyArt(a.sheetKey, a.glowKey, a.upperKey, a.upperGlowKey, a.upperOrigin);
    });
  }

  private setBodyArt(sheet: string, glow: string | null, upper: string, upperGlow: string | null, origin: { x: number; y: number }): void {
    const tex = this.scene.textures;
    if (!tex.exists(sheet)) return;
    this.body.setTexture(sheet, this.body.frame.name);
    if (this.bodyGlow && glow && tex.exists(glow)) this.bodyGlow.setTexture(glow, this.bodyGlow.frame.name);
    if (tex.exists(upper)) {
      for (const part of [this.upperF, this.upperR]) {
        part.sprite.setTexture(upper).setOrigin(origin.x, origin.y);
        part.glow?.setTexture(upperGlow && tex.exists(upperGlow) ? upperGlow : upper).setOrigin(origin.x, origin.y).setVisible(!!upperGlow);
      }
    }
    this.applyTints();
    this.applyFrame();
  }

  setEquipment(ids: readonly string[]): void {
    if (this.destroyed) return;
    for (const p of this.pieces) {
      p.sprite.destroy();
      p.glow?.destroy();
    }
    this.pieces = [];
    // Painted equipment replaces rig parts (re-baked sheets); only the rest uses add-on pieces.
    const painted = this.rig.placeholder ? [] : paintedEquipment(this.mechId, ids);
    this.applyLoadout(painted);
    let reactorOverride: number | null = null;
    for (const id of ids) {
      if (painted.includes(id)) continue;
      const vis = art.visuals[id];
      let list: VisualPiece[] = [];
      if (vis && vis.pieces.length > 0) {
        list = vis.pieces.filter((p) => this.scene.textures.exists(p.key));
        if (vis.reactorGlow !== undefined) reactorOverride = vis.reactorGlow;
      }
      if (list.length === 0) {
        const slot = EQUIP_SLOT_OF[id as EquipId];
        if (slot) list = placeholderPieces(this.scene, slot);
      }
      for (const piece of list) {
        const layer = piece.layer === 'rear' ? this.rearLayer : this.frontLayer;
        const sprite = this.scene.add.image(0, 0, piece.key).setOrigin(piece.originX, piece.originY);
        sprite.setScale(piece.scale ?? 1);
        layer.add(sprite);
        let glow: Phaser.GameObjects.Image | null = null;
        if (piece.glowKey && this.scene.textures.exists(piece.glowKey)) {
          glow = this.scene.add.image(0, 0, piece.glowKey).setOrigin(piece.originX, piece.originY);
          glow.setScale(piece.scale ?? 1).setBlendMode(Phaser.BlendModes.ADD);
          layer.add(glow);
        }
        this.pieces.push({ piece, sprite, glow });
      }
    }
    this.reactorOverride = reactorOverride;
    this.reactor = reactorOverride ?? this.baseReactor;
    this.applyTints();
    this.applyFrame();
  }

  setReactorColor(color: number): void {
    this.baseReactor = color;
    // An equipped reactor housing keeps its own glow colour.
    this.reactor = this.reactorOverride ?? color;
    this.applyTints();
  }

  play(anim: AnimKey, opts?: { restart?: boolean; timeScale?: number }): void {
    if (this.destroyed) return;
    const ts = opts?.timeScale ?? 1;
    if (!this.phaseMode && anim === this._anim && !opts?.restart) {
      this.timeScale = ts;
      return;
    }
    this._anim = anim;
    this.animSrc = this.rig.anims[anim];
    this.phaseMode = false;
    this.timeScale = ts;
    // A reversed loop (backpedal) starts from its end so frame 0 is not shown twice.
    this.animTime = ts < 0 && this.animSrc.loop ? this.animSrc.frames.length - 1e-3 : 0;
    this._animDone = false;
    this.frame = this.pickFrame();
    this.applyFrame();
  }

  setPhase(anim: AnimKey, t: number): void {
    if (this.destroyed) return;
    if (anim !== this._anim || !this.phaseMode) {
      this._anim = anim;
      this.animSrc = this.rig.anims[anim];
      this.phaseMode = true;
      this._animDone = false;
    }
    const n = this.animSrc.frames.length;
    this.frame = Math.min(n - 1, Math.floor(clamp01(t) * n));
  }

  recoil(amount: number): void {
    this.recoilAmount = clamp01(Math.max(amount, this.recoilT < RECOIL_TIME ? this.recoilAmount : 0));
    this.recoilT = 0;
  }

  /** Decaying 0..1 kick from the weapon runtime (adds pull-back and muzzle rise). */
  setKick(k: number): void {
    this.kick = clamp01(k);
  }

  /** Landing squash (negative) / jump stretch (positive) as a fraction of height. */
  setSquash(s: number): void {
    this.squash = clamp(s, -0.08, 0.08);
  }

  /**
   * Rotate the whole mech about its body centre (world angle, clockwise +), e.g. flips.
   * The aimed arm compensates, so the weapon keeps pointing at the aim.
   */
  /** Current whole-body spin (rad) and flip tuck (0..1), e.g. for network sync. */
  get spinAngle(): number {
    return this.spin;
  }

  get tuckAmount(): number {
    return this.tuck;
  }

  setSpin(angle: number, tuck = 0): void {
    this.spin = Number.isFinite(angle) ? angle : 0;
    this.tuck = clamp01(tuck);
  }

  flash(color: number = 0xffffff, ms = 90): void {
    if (this.destroyed) return;
    const reduced = save.settings.reducedFlashing;
    const fill = reduced ? mix(color, 0x7a8394, 0.55) : color;
    this.flashT = ((reduced ? 0.6 : 1) * ms) / 1000;
    for (const s of this.solidSprites()) s.setTintFill(fill);
  }

  flinch(): void {
    this.flinchT = FLINCH_TIME;
  }

  setAlpha(a: number): void {
    if (!this.destroyed) this.root.setAlpha(a);
  }

  setVisible(v: boolean): void {
    if (!this.destroyed) this.root.setVisible(v);
  }

  setScale(s: number): void {
    // The mech's own display size (MechDef.viewScale) rides on top of every requested scale.
    this.scale = s * this.viewScale;
    this.setFacing(this.facing);
  }

  setDepth(d: number): void {
    if (!this.destroyed) this.root.setDepth(d);
  }

  attachWorld(key: AttachKey): { x: number; y: number; rot: number } {
    const p = this.meta.attach[key];
    const lx = p ? p.x - this.rig.footAnchor.x : 0;
    const ly = p ? p.y - this.rig.footAnchor.y : -44;
    const out = { x: 0, y: 0, rot: 0 };
    this.toWorld(lx, ly, out);
    out.rot = this.facing * ((p?.rot ?? 0) + this.inner.rotation);
    return out;
  }

  muzzleWorld(): { x: number; y: number; angle: number } {
    const out = { x: 0, y: 0, angle: 0 };
    this.muzzleInto(out);
    return out;
  }

  /** Allocation-free muzzle query. */
  muzzleInto(out: { x: number; y: number; angle: number }): void {
    const m = this.weaponArt?.muzzle ?? { x: 26, y: -2 };
    const c = Math.cos(this.weaponRot);
    const s = Math.sin(this.weaponRot);
    const lx = this.gripX + m.x * c - m.y * s;
    const ly = this.gripY + m.x * s + m.y * c;
    this.toWorld(lx, ly, out);
    out.angle = toWorldAngle(wrapAngle(this.weaponRot + this.inner.rotation), this.facing);
  }

  update(dt: number): void {
    if (this.destroyed) return;
    // Frame clock.
    if (!this.phaseMode) {
      const a = this.animSrc;
      const n = a.frames.length;
      this.animTime += dt * a.fps * this.timeScale;
      if (a.loop) {
        this.animTime = ((this.animTime % n) + n) % n;
      } else if (this.animTime >= n) {
        this.animTime = n;
        this._animDone = true;
      } else if (this.animTime < 0) {
        this.animTime = 0;
      }
      this.frame = this.pickFrame();
    }
    // Overlays.
    if (this.recoilT < RECOIL_TIME) this.recoilT = Math.min(RECOIL_TIME, this.recoilT + dt);
    this.flinchT = Math.max(0, this.flinchT - dt);
    if (this.flashT > 0) {
      this.flashT -= dt;
      if (this.flashT <= 0) this.applyTints();
    }
    this.applyFrame();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.pieces = [];
    // Safe after scene shutdown: Phaser ignores destroy() on objects without a scene.
    this.root.destroy();
  }

  // ── Internals ──

  private pickFrame(): number {
    const n = this.animSrc.frames.length;
    return Math.min(n - 1, Math.max(0, Math.floor(this.animTime)));
  }

  /** Local (foot-anchor origin, right-facing) → world, through the inner and root transforms. */
  private toWorld(lx: number, ly: number, out: { x: number; y: number }): void {
    const inn = this.inner;
    const sx = lx * inn.scaleX;
    const sy = ly * inn.scaleY;
    const c = Math.cos(inn.rotation);
    const s = Math.sin(inn.rotation);
    const ix = inn.x + sx * c - sy * s;
    const iy = inn.y + sx * s + sy * c;
    out.x = this.root.x + ix * this.root.scaleX;
    out.y = this.root.y + iy * this.root.scaleY;
  }

  private solidSprites(): (Phaser.GameObjects.Image | Phaser.GameObjects.Sprite)[] {
    const list: (Phaser.GameObjects.Image | Phaser.GameObjects.Sprite)[] = [
      this.body,
      this.upperR.sprite,
      this.foreR.sprite,
      this.upperF.sprite,
      this.foreF.sprite,
      this.weapon,
    ];
    for (const p of this.pieces) list.push(p.sprite);
    return list;
  }

  /** Resting tints: rear parts darkened, glows in the reactor colour. */
  private applyTints(): void {
    if (this.destroyed) return;
    const rear = this.rig.rearTint;
    this.body.clearTint();
    this.upperF.sprite.clearTint();
    this.foreF.sprite.clearTint();
    this.weapon.clearTint();
    this.upperR.sprite.setTint(rear);
    this.foreR.sprite.setTint(rear);
    const glow = this.reactor;
    this.bodyGlow?.setTint(glow);
    this.upperF.glow?.setTint(glow);
    this.foreF.glow?.setTint(glow);
    this.upperR.glow?.setTint(mix(glow, 0x000000, 0.35));
    this.foreR.glow?.setTint(mix(glow, 0x000000, 0.35));
    this.weaponGlow.setTint(this.weaponArt?.glowColor ?? glow);
    for (const p of this.pieces) {
      if (p.piece.layer === 'rear') p.sprite.setTint(REAR_EQUIP_TINT);
      else p.sprite.clearTint();
      p.glow?.setTint(p.piece.layer === 'rear' ? mix(glow, 0x000000, 0.35) : glow);
    }
  }

  /** Pose every part for the current frame. */
  private applyFrame(): void {
    if (this.destroyed) return;
    const r = this.rig;
    const ax = r.footAnchor.x;
    const ay = r.footAnchor.y;

    // Shoot layer: progress through the shoot sheet while the recoil window is open.
    const shoot = r.anims.shoot;
    const recoiling = this.recoilT < RECOIL_TIME && this.recoilAmount > 0;
    let recoilPx = this.kick * KICK_PX;
    let lift = this.kick * KICK_LIFT;
    let src = this.animSrc;
    let frame = this.frame;
    if (recoiling) {
      const si = Math.min(shoot.frames.length - 1, Math.floor((this.recoilT / RECOIL_TIME) * shoot.frames.length));
      const sm = shoot.meta[si];
      recoilPx += (sm.recoil ?? 0) * this.recoilAmount;
      lift += (sm.lift ?? 0) * this.recoilAmount;
      // Standing still: the whole body plays the shoot sheet. Otherwise only the arms (legs keep running).
      if (this._anim === 'idle' && !this.phaseMode) {
        src = shoot;
        frame = si;
      }
    }
    const meta = src.meta[frame] ?? src.meta[0];
    this.meta = meta;
    const tf = src.frames[frame] ?? src.frames[0];
    this.body.setFrame(tf);
    this.bodyGlow?.setFrame(tf);

    // Flinch + squash on the inner container (feet stay planted: origin is the foot anchor).
    const k = this.flinchT / FLINCH_TIME;
    const pulse = k * k;
    const sq = this.squash;
    // Spin: the root is mirrored by facing, so a world angle is `facing ×` the local one.
    // Offset the inner container so the body centre stays put while it turns.
    const spinL = this.spin * this.facing;
    const sc = Math.cos(spinL);
    const ss = Math.sin(spinL);
    // Tuck (flips): the body compacts slightly about its feet and turns about the compact body's centre.
    const ts = 1 - TUCK_SHRINK * this.tuck;
    const P = SPIN_PIVOT_Y * ts;
    this.inner.setPosition(-3.5 * pulse + P * ss, P - P * sc);
    this.inner.setRotation(-0.075 * pulse + spinL);
    this.inner.setScale((1 - sq * 0.6) * ts, (1 + sq) * ts);

    // Equipment pieces.
    for (const p of this.pieces) {
      const pc = p.piece;
      const at = meta.attach[pc.attach];
      if (!at) continue;
      let dx = pc.dx;
      let dy = pc.dy;
      let rot = pc.rot;
      if (pc.followRot) {
        const c = Math.cos(at.rot);
        const s = Math.sin(at.rot);
        dx = pc.dx * c - pc.dy * s;
        dy = pc.dx * s + pc.dy * c;
        rot += at.rot;
      }
      p.sprite.setPosition(at.x - ax + dx, at.y - ay + dy).setRotation(rot);
      p.glow?.setPosition(p.sprite.x, p.sprite.y).setRotation(rot);
    }

    // Arms.
    const sF = meta.attach.shoulderF;
    const sR = meta.attach.shoulderR;
    const sfx = (sF?.x ?? ax + 4) - ax;
    const sfy = (sF?.y ?? ay - 60) - ay;
    const srx = (sR?.x ?? ax - 5) - ax;
    const sry = (sR?.y ?? ay - 61) - ay;
    const l1 = r.upperLen;
    const l2 = r.foreLen;
    const w = clamp01(meta.aimWeight);

    // While spinning, the open (untucked) body keeps the weapon on target by aiming relative to
    // the turned body; a tucked body carries the weapon round with it (blended by the tuck).
    const bodyAim = clamp(toLocalAngle(this.aimWorld, this.facing), -AIM_LIMIT, AIM_LIMIT);
    const aimLocal = spinL === 0 ? bodyAim : lerpAngle(wrapAngle(toLocalAngle(this.aimWorld, this.facing) - spinL), bodyAim, this.tuck);
    const weaponAim = aimLocal + lift;
    // Aimed front arm: the hand sits on the aim ray from the shoulder (dropped below it by the
    // weapon's holdDrop so a tall receiver clears the face), pulled back by recoil.
    const ac = Math.cos(aimLocal);
    const as = Math.sin(aimLocal);
    const drop = this.holdDrop;
    const fg0 = this.weaponArt?.foregrip ?? { x: 10, y: 1 };
    const minHd = Math.abs(l1 - l2) + 2;
    // Never hold the gun further out than the support hand can still reach its foregrip.
    const hdFit = supportReachLimit(sfx - srx, sfy - sry, ac, as, drop, fg0.x, fg0.y, (l1 + l2) * SUPPORT_STRETCH);
    const hd = Math.max(minHd, Math.min((l1 + l2) * this.holdReach, hdFit) - recoilPx);
    solveTwoBone(sfx, sfy, sfx + ac * hd - as * drop, sfy + as * hd + ac * drop, l1, l2, 1, ik);
    const upF = lerpAngle(meta.armF.upper, ik.upper, w);
    const foF = lerpAngle(meta.armF.fore, ik.fore, w);
    forwardKinematics(sfx, sfy, upF, foF, l1, l2, fk);
    this.place(this.upperF, sfx, sfy, upF);
    this.place(this.foreF, fk.ex, fk.ey, foF);
    this.gripX = fk.hx;
    this.gripY = fk.hy;
    // Unaimed poses carry the weapon along the forearm.
    this.weaponRot = lerpAngle(foF, weaponAim, w);

    if (this.weaponArt) {
      this.weapon.setPosition(this.gripX, this.gripY).setRotation(this.weaponRot);
      this.weaponGlow.setPosition(this.gripX, this.gripY).setRotation(this.weaponRot);
    }

    // Rear arm: reach the foregrip (or the grip itself without a weapon).
    const fg = this.weaponArt?.foregrip ?? { x: 10, y: 1 };
    const c = Math.cos(this.weaponRot);
    const s = Math.sin(this.weaponRot);
    const fx = this.gripX + fg.x * c - fg.y * s;
    const fy = this.gripY + fg.x * s + fg.y * c;
    solveTwoBone(srx, sry, fx, fy, l1, l2, 1, ik);
    const upR = lerpAngle(meta.armR.upper, ik.upper, w);
    const foR = lerpAngle(meta.armR.fore, ik.fore, w);
    forwardKinematics(srx, sry, upR, foR, l1, l2, fk);
    this.place(this.upperR, srx, sry, upR);
    this.place(this.foreR, fk.ex, fk.ey, foR);
  }

  private place(part: ArmPart, x: number, y: number, angle: number): void {
    const rot = angle + part.rotOffset;
    part.sprite.setPosition(x, y).setRotation(rot);
    part.glow?.setPosition(x, y).setRotation(rot);
  }
}

export function createMechView(scene: Phaser.Scene, mechId: MechId): MechViewApi {
  return new MechView(scene, mechId);
}
